import * as React from "react";
import { GatsbyBrowser } from "gatsby";

import {
  initSentry,
  initTracking,
  saveMarketingParamsToLocalStorage,
  trackEvent,
} from "./lib/tracking-lib";
import { upsertWebSession } from "./services/webSession";
import { getTrackers } from "./config";
import { isTrackerOn } from "./lib/tracking-lib/activeTrackers";
import { SiteConfig, SiteMetadata } from "./types";
import { getFunnelIDFromURL } from "./utils/getFunnelIDFromURL";
import { removeJumpTokenFromURL, takeJumpTokenFromURL } from "./lib/checkoutSession";

export type GatsbyBrowserConfig = {
  siteConfig: SiteConfig;
  siteMetadata: SiteMetadata;
  RootWrapperComponent?: React.ComponentType<{ children: React.ReactNode }>;
  PageWrapperComponent?: React.ComponentType<{ children: React.ReactNode }>;
};

export function createOnClientEntry(
  config: GatsbyBrowserConfig,
): GatsbyBrowser["onClientEntry"] {
  return () => {
    // Read only. onRouteUpdate takes it off the URL after the first render.
    takeJumpTokenFromURL();

    const trackers = getTrackers();

    if (isTrackerOn("sentry")) {
      initSentry(trackers.sentry.dsn.value, config.siteMetadata.appVersion);
    }

    // Save marketing params from URL to localStorage for later retrievals
    saveMarketingParamsToLocalStorage();

    initTracking({
      gtm: {
        // GTM ID is set in gatsby-config.js
        ga4Config: {
          server_container_url: trackers.gtm.ga4ServerContainerURL?.value ?? "",
        },
      },
      posthog: {
        apiHost: trackers.posthog.host.value,
        token: trackers.posthog.key.value,
        enabled: isTrackerOn("posthog"),
      },
      globalContext: {
        abTestName: config.siteMetadata.abTestName || "",
        abTestValue: config.siteMetadata.abTestValue || "",
        appVersion: config.siteMetadata.appVersion,
        appName: config.siteMetadata.appName,
        brand: config.siteMetadata.brandName,
        environment:
          config.siteMetadata.environment ||
          process.env.NODE_ENV ||
          "development",
        funnelID: getFunnelIDFromURL(),
      },
    });
  };
}

export function createOnRouteUpdate(): GatsbyBrowser["onRouteUpdate"] {
  // The first route update is the pageload itself, which onClientEntry already
  // upserted. Counted rather than read from `prevLocation`, which Gatsby also
  // sends as null whenever its RouteUpdates component (re)mounts.
  let pageloadSeen = false;

  return ({ location }) => {
    // In-app page changes (Gatsby <Link>) skip onClientEntry, so the backend
    // would never see them — e.g. the footer's legal pages.
    if (pageloadSeen) {
      upsertWebSession().catch((err) => {
        console.error("Failed to upsert web session:", err);
      });
    } else {
      // Fires after the first render, so the URL is now safe to change.
      removeJumpTokenFromURL();
    }
    pageloadSeen = true;

    trackEvent({ name: "page-view", properties: { path: location.pathname } });
  };
}
